<template>
<div v-if="data" class="residual-wrapper">
  <div class="factor-selector-list-wrapper">
    <div class="factor-selector-list-header">选择因子</div>

    <div class="divider"></div>

    <div class="factor-selector-list-body">
      <ul class="factor-group-select-list">
        <li
          v-for="factorGroup in formatFactorGroups"
          :key="factorGroup.id"
        >
          <div class="factor-group-name">{{factorGroup.name}}</div>

          <ul class="factor-select-list">
            <li
              v-for="factor in factorGroup.children"
              :key="factor.id"
              :class="{
              selected: factor.id === data.factorId
            }"
              @click="factorIdChange(factor.id)"
            >{{factor.name}}</li>
          </ul>
        </li>
      </ul>
    </div>
  </div>

  <div class="residual-chart-wrapper">
    <div style="display:flex;justify-content:flex-end;padding:5px 10px;">
      <el-checkbox v-model="data.scaleY" @change="onScaleYChanged">放大Y轴</el-checkbox>
    </div>
    <div>
      <e-chart ref="chart" />
    </div>
  </div>

  <div class="residual-table-wrapper" style="border:0;">
    <div style="display:grid;grid-template-columns:max-content 80px max-content;grid-column-gap:5px;justify-content:flex-end;align-items:center;font-size:14px;padding-top:5px;color:#606266;">
      <label>显示残差值为</label>
      <el-input v-model="data.minResidual" type="number" size="mini" />
      <span>以上的数据</span>
    </div>
    <div>
      <el-table
        ref="table"
        :data="list"
        height="100%"
        border
        stripe
        highlight-current-row
        header-row-class-name="custom-table-header"
        class="custom-table no-inner-border"
        style="width: 100%"
      >
        <el-table-column prop="dataTime" label="时间" width="130" />
        <el-table-column prop="residual" label="残差" />
      </el-table>
    </div>
  </div>
</div>
</template>

<script>
import BaseResult from './BaseResult'
import EChart from '../../../../../../components/Charts/ECharts'
import ChartStyle from '../../../../../../components/DataChart/ChartStyle'

const dataKey = 'ResidualAnalysis'

export default {
  name: 'ResidualAnalysis',
  components: {EChart},
  label: '残差分析',
  dataKey,
  extends: BaseResult,
  computed: {
    list () {
      const {data} = this
      let list = Object.values(data.data[data.factorId])
      let {minResidual} = data

      if (Number.isFinite(parseFloat(minResidual))) {
        minResidual = Number(minResidual)
        list = list.filter(data => Math.abs(data.residual) >= minResidual)
      }

      return list
    }
  },
  methods: {
    renderChart () {
      const {data} = this
      const list = Object.values(data.data[data.factorId]).sort((a, b) => a.scaledResidual - b.scaledResidual)
      const {length} = list

      if (length === 0) {
        return this.$refs.chart.clear()
      }

      const min = list[0].scaledResidual
      const max = list[length - 1].scaledResidual
      const categories = []
      const {COLORS} = ChartStyle

      for (let i = -0.25; i > min;) {
        categories.unshift({
          max: i,
          min: i -= 0.5,
          value: 0,
          itemStyle: {
            color: i < -3.25 ? COLORS[1] : COLORS[0]
          }
        })
      }

      categories.push({
        min: -0.25,
        max: 0.25,
        value: 0,
        itemStyle: {
          color: COLORS[0]
        }
      })

      for (let i = 0.25; i < max;) {
        categories.push({
          min: i,
          max: i += 0.5,
          value: 0,
          itemStyle: {
            color: i > 3.25 ? COLORS[1] : COLORS[0]
          }
        })
      }

      const categoriesLen = categories.length - 1

      for (let i = 0; i < length; i++) {
        const data = list[i]
        const {scaledResidual} = data
        const category = categories.find(category => category.min <= scaledResidual && scaledResidual < category.max) || categories[categoriesLen]
        category.value++
      }

      categories.forEach(category => {
        const {value} = category
        category.value = value === 0 ? null : (value / length * 100)
      })

      const factor = this.factorMap[data.factorId]
      const chartOpt = {
        title: {
          text: factor.name,
          top: 20,
          left: 'center'
        },
        toolbox: {
          feature: {
            saveAsImage: {}
          },
          right: 23
        },
        tooltip: {
          appendToBody: true,
          formatter: param => `${param.marker}<span>${param.name}: </span><span>${Number.isFinite(param.value) ? param.value.toFixed(2) : '--'}%</span>`
        },
        grid: {
          top: 70,
          right: 40,
          bottom: 40,
          left: 60,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          name: `Scaled Residuals`,
          nameLocation: 'center',
          nameGap: 90,
          data: categories.map(category => `${category.min}~${category.max}`),
          splitLine: {
            show: false
          },
          axisLine: {
            onZero: false
          },
          axisLabel: {
            rotate: 90
          }
        },
        yAxis: {
          type: 'value',
          name: `%`,
          nameLocation: 'center',
          nameGap: 40,
          max: data.scaleY ? undefined : 100,
          splitLine: {
            show: false
          },
          axisLine: {
            show: true,
            onZero: false
          },
          axisTick: {
            show: true
          }
        },
        series: [{
          name: '占比',
          type: 'bar',
          barMaxWidth: 30,
          data: categories.map(category => category)
        }]
      }

      this.$refs.chart.setOpt(chartOpt, true)
    },
    factorIdChange (factorId) {
      const {data} = this

      if (data.factorId !== factorId) {
        data.factorId = factorId
        this.renderChart()
      }
    },
    onScaleYChanged () {
      this.renderChart()
    }
  },
  created () {
    const {data} = this

    if (data) {
      setTimeout(() => {
        this.renderChart()
      }, 0)
    } else {
      this.changeLoadStatus(true)

      this.$api.PMFAnalysis.residual(this.taskId).then(data => {
        this.$emit('update:data', {
          factorId: this.condition.factorIds[0],
          minResidual: 3,
          scaleY: false,
          data: Object.freeze((data || []).reduce((map, data) => {
            const dataTime = new Date(data.dataTime)
            const {factorId} = data
            const ts = dataTime.getTime()
            map[factorId][ts] = Object.freeze({
              dataTime: dataTime.format('YYYY/MM/DD HH'),
              ts,
              factorId,
              predicted: data.predicted,
              residual: data.residual,
              scaledResidual: data.scaledResidual
            })

            return map
          }, this.condition.factorIds.reduce((map, factorId) => {
            map[factorId] = {}
            return map
          }, {})))
        })

        setTimeout(() => {
          this.renderChart()
        }, 0)
      }).catch(console.error).finally(() => {
        this.changeLoadStatus(false)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.residual-wrapper {
  display: grid;
  grid-template-columns: minmax(max-content, 250px) auto max-content;
  grid-column-gap: 12px;

  > * {
    min: {
      width: 0;
      height: 0;
    };
    border: 1px solid #EBEEF5;
  }
}

.factor-selector-list-wrapper {
  display: grid;
  grid-template-rows: max-content 1px auto;

  > * {
    min: {
      width: 0;
      height: 0;
    }
  }

  > .factor-selector-list-header {
    font-weight: 700;
    height: 35px;
    line-height: 35px;
    padding-left: 10px;
    color: #303133;
    background-color: #f9f9f9;
  }

  > .factor-selector-list-body {
    overflow: auto;
  }

  .factor-group-select-list {
    font: {
      size: 14px;
    };
    margin: 0;
    padding: 0;
    line-height: 32px;
    list-style: none;

    > li {
      > .factor-group-name {
        font-weight: bolder;
        padding: 0 10px;
        /*text-align: center;*/
        cursor: default;
      }
    }
  }

  .factor-select-list {
    font: {
      size: 14px;
    };
    margin: 0;
    padding: 0;
    line-height: 32px;
    list-style: none;

    > li {
      padding: 0 20px;
      cursor: pointer;

      &:hover {
        background-color: #F5F7FA;
      }

      &.selected {
        background-color: #ecf5ff;
      }
    }
  }
}

.residual-table-wrapper {
  display: grid;
  grid-template-rows: max-content auto;
  grid-row-gap: 12px;

  > * {
    min: {
      width: 0;
      height: 0;
    }
  }
}

.residual-chart-wrapper {
  display: grid;
  grid-template-rows: max-content auto;
  grid-column-gap: 12px;

  > * {
    min: {
      width: 0;
      height: 0;
    }
  }
}

.divider {
  background-color: #EBEEF5;
}
</style>



// WEBPACK FOOTER //
// src/views/04Statistical-Analysis/11PMF-Analysis/01PM/01Run/ResultComponents/01ResidualAnalysis.vue